import type { StringMap } from 'i18next'
import { html, LitElement, type TemplateResult } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import equals from 'ramda/src/equals'
import type { SupportedLocale } from 'zaptic-types'
import { omit, validateIso8601String } from '../../../lib/helpers'
import { assertExhaustion, type UUID } from '../../../lib/typeHelpers.no-deps'
import { languageSensitiveListFormatter, replaceTokensWithLitHtml } from '../../helpers/lit-template'

export enum Events {
    onClickUser = 'onClickUser',
}

export interface CustomEvents {
    [Events.onClickUser]: CustomEvent<number>
}

export type Content = {
    authorId: number
    authorDeleted: boolean
    authorName: string
} & (
    | { type: 'bulk-edited'; bulkEditId: UUID; unitCount: number }
    | { type: 'data-added' | 'id-code-added'; key: string; value: string }
    | { type: 'data-removed' | 'id-code-removed'; key: string }
    | {
          type: 'data-value-updated' | 'id-code-value-updated'
          key: string
          oldValue: string
          newValue: string
      }
    | { type: 'experts-added' | 'experts-removed'; experts: Array<{ id: number; isDeleted: boolean; name: string }> }
    | { type: 'image-added'; value: string }
    | { type: 'image-removed' }
    | { type: 'image-value-updated'; oldValue: string; newValue: string }
    | { type: 'parent-added' | 'parent-removed'; unitName: string }
    | { type: 'rename-unit'; oldName: string; newName: string }
    | { type: 'type-changed'; oldName: string; newName: string }
    | { type: 'unit-created' }
    | { type: 'unit-deleted' }
)

function dateTimeHasChanged(newValue: string, oldValue: string) {
    if (!validateIso8601String(newValue)) {
        return false
    }
    return newValue !== oldValue
}

function contenthasChanged(newValue: Content, oldValue: Content) {
    return !equals(newValue, oldValue)
}

@customElement('unit-timeline-event')
export class UnitTimelineEvent extends LitElement {
    @property({ hasChanged: dateTimeHasChanged })
    dateTime: string // ISO 8601 string

    @property()
    locale: SupportedLocale

    @property({ hasChanged: contenthasChanged, type: Object })
    content: Content

    @property()
    authorNameType: 'link' | 'plain' = 'plain'

    private onClickUser = (userId: number, userDeleted: boolean) => {
        if (userDeleted) {
            return
        }
        this.dispatchEvent(new CustomEvent<number>(Events.onClickUser, { detail: userId, composed: true }))
    }

    private onClickAuthor = (userId: number) => {
        this.onClickUser(userId, this.content.authorDeleted)
    }

    private isUserClickable(userDeleted: boolean) {
        return !userDeleted
    }

    private isAuthorClickable() {
        return this.isUserClickable(this.content.authorDeleted)
    }

    private getIconName() {
        return 'edit' as const
    }

    private getMessageTranslationSlug(): string {
        switch (this.content.type) {
            case 'bulk-edited':
                return 'shared:unitTimelineEvent.bulkEdited'
            case 'data-added':
                return 'shared:unitTimelineEvent.dataAdded'
            case 'data-removed':
                return 'shared:unitTimelineEvent.dataRemoved'
            case 'data-value-updated':
                return 'shared:unitTimelineEvent.dataValueUpdated'
            case 'experts-added':
                return this.content.experts.length > 1
                    ? 'shared:unitTimelineEvent.expertsAdded.plural'
                    : 'shared:unitTimelineEvent.expertsAdded.singular'
            case 'experts-removed':
                return this.content.experts.length > 1
                    ? 'shared:unitTimelineEvent.expertsRemoved.plural'
                    : 'shared:unitTimelineEvent.expertsRemoved.singular'
            case 'id-code-added':
                return 'shared:unitTimelineEvent.idCodeAdded'
            case 'id-code-removed':
                return 'shared:unitTimelineEvent.idCodeRemoved'
            case 'id-code-value-updated':
                return 'shared:unitTimelineEvent.idCodeValueUpdated'
            case 'image-added':
                return 'shared:unitTimelineEvent.imageAdded'
            case 'image-removed':
                return 'shared:unitTimelineEvent.imageRemoved'
            case 'image-value-updated':
                return 'shared:unitTimelineEvent.imageValueUpdated'
            case 'parent-added':
                return 'shared:unitTimelineEvent.parentAdded'
            case 'parent-removed':
                return 'shared:unitTimelineEvent.parentRemoved'
            case 'rename-unit':
                return 'shared:unitTimelineEvent.renameUnit'
            case 'type-changed':
                return 'shared:unitTimelineEvent.typeChanged'
            case 'unit-created':
                return 'shared:unitTimelineEvent.created'
            case 'unit-deleted':
                return 'shared:unitTimelineEvent.deleted'
            default:
                assertExhaustion(this.content, 'unsupported type')
        }
    }

    private getTranslationArguments(): StringMap {
        // `authorName` is omitted from translation args because it's replaced by an HTML element in `mapTokenToElement`.
        if (this.content.type === 'experts-added' || this.content.type === 'experts-removed') {
            // `experts` must also be omitted for `experts-added` and `experts-removed` events.
            return omit(this.content, 'authorName', 'experts')
        }
        return omit(this.content, 'authorName')
    }

    private getTranslationTokensForCurrentContentType() {
        const tokens = ['{{authorName}}']

        switch (this.content.type) {
            case 'bulk-edited':
                tokens.push('{{otherUnitsCountButton}}')
                break
            case 'experts-added':
            case 'experts-removed':
                tokens.push('{{experts}}')
                break
        }

        return tokens
    }

    private mapTokenToElement(token: string): TemplateResult {
        switch (token) {
            case '{{authorName}}':
                return html`<zaptic-timeline-user
                    @click=${() => this.onClickAuthor(this.content.authorId)}
                    name=${this.content.authorName}
                    type=${this.isAuthorClickable() ? this.authorNameType : 'plain'}
                ></zaptic-timeline-user>`

            case '{{experts}}':
                if (this.content.type !== 'experts-added' && this.content.type !== 'experts-removed') {
                    throw new Error(`Unexpected token: ${token} for content type: ${this.content.type}`)
                }
                return languageSensitiveListFormatter(
                    this.content.experts,
                    (expert) =>
                        html`<zaptic-timeline-user
                            @click=${() => this.onClickUser(expert.id, expert.isDeleted)}
                            name=${expert.name}
                            type=${this.isUserClickable(expert.isDeleted) ? this.authorNameType : 'plain'}
                        ></zaptic-timeline-user>`
                )

            case '{{otherUnitsCountButton}}':
                if (this.content.type !== 'bulk-edited') {
                    throw new Error(`Unexpected token: ${token} for content type: ${this.content.type}`)
                }
                const otherUnitsCount = this.content.unitCount - 1
                return this.getBulkEditedCountElement(otherUnitsCount, this.content.bulkEditId)

            default:
                throw new Error(`Unsupported token: ${token}`)
        }
    }

    private Message() {
        const translationArgs = {
            ...this.getTranslationArguments(),
            openingTagForName: '<b class="has-font-weight-bold">',
            closingTagForName: '</b>',
            interpolation: {
                escapeValue: true, // => get html-escaped, unless they are prefixed with '-' in the translation string, eg {{- openingTagForName}}
            },
        }
        const translated = window.i18n(this.getMessageTranslationSlug(), translationArgs)
        const translationTokens = this.getTranslationTokensForCurrentContentType()
        const translatedElement = replaceTokensWithLitHtml(
            translated,
            translationTokens,
            this.mapTokenToElement.bind(this)
        )

        if (this.content.type === 'bulk-edited') {
            const downloadCsvElement = this.getBulkEditedDownloadCsvElement(this.content.bulkEditId)
            return html`
                <div class="bulk-edited-message">
                    <span>${translatedElement}</span>
                    ${downloadCsvElement}
                </div>
            `
        }

        return translatedElement
    }

    // can be overriden by derived classes (eg for portal-specific behaviour)
    protected getBulkEditedCountElement(otherUnitsCount: number, _bulkEditId: UUID) {
        return html`${otherUnitsCount}+`
    }

    // can be overriden by derived classes (eg for portal-specific behaviour)
    protected getBulkEditedDownloadCsvElement(_bulkEditId: UUID) {
        return html``
    }

    protected createRenderRoot() {
        return this
    }

    public render() {
        return html`
            <zaptic-timeline-event dateTime=${this.dateTime} iconName=${this.getIconName()}>
                <div slot="text">${this.Message()}</div>
            </zaptic-timeline-event>
        `
    }
}
